import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import HrLayout from "../HrLayout";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
} from "recharts";

export default function HrDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [office, setOffice] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setErr("");

      const [dashRes, officeRes] = await Promise.all([
        api.get("/hr/dashboard"),
        api.get("/hr/office-location"),
      ]);

      // Fix: Ensure deptHeadcount count is number
      const deptHeadcount = dashRes.data.charts?.deptHeadcount || [];
      const deptBars = deptHeadcount.map((d) => ({
        name: d.name || "—",
        count: Number(d.count) || 0,
      }));

      setData({ ...dashRes.data, charts: { ...dashRes.data.charts, deptHeadcount: deptBars } });
      setOffice(officeRes.data);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load HR dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const pieData = useMemo(() => data?.charts?.leavePie || [], [data]);
  const trend = useMemo(() => data?.charts?.attendanceTrend || [], [data]);
  const deptBars = useMemo(() => data?.charts?.deptHeadcount || [], [data]);

  const pieColors = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#06B6D4"];

  return (
    <HrLayout title="HR Dashboard" subtitle="SaaS overview, alerts & analytics">
      {err && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {err}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">HR Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Live attendance + leave approvals + department overview</p>
          {office && (
            <div className="mt-3 inline-flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200">🏢 {office.name}</span>
              <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200">
                📍 {office.city || ""} {office.state || ""} {office.country || ""}
              </span>
              <span className="px-2 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700">
                Geo: {office.officeLat ?? "--"}, {office.officeLng ?? "--"}
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">Refresh</button>
          <span className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold">HR SaaS</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <StatCard title="Total Employees" value={data?.stats?.totalEmployees ?? "--"} hint="Active employees" />
        <StatCard title="Today Present" value={data?.stats?.presentToday ?? "--"} hint="Punch / Present" accent="emerald" />
        <StatCard title="Today Absent" value={data?.stats?.absentToday ?? "--"} hint="Auto computed" accent="rose" />
        <StatCard title="Late Today" value={data?.stats?.lateToday ?? "--"} hint={`After ${data?.labels?.lateAfter || "10:15"}`} accent="amber" />
        <StatCard title="Pending Leaves" value={data?.stats?.pendingLeaves ?? "--"} hint="Approval needed" accent="indigo" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        {/* Attendance Trend */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm xl:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-800">Attendance Trend (Last 30 Days)</h2>
            <span className="text-xs text-slate-500">Present count per day</span>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="present" stroke="#6366F1" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Leave Pie */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-800">Leave Status (This Month)</h2>
            <span className="text-xs text-slate-500">Pie</span>
          </div>
          <div className="h-72 flex items-center justify-center">
            {pieData.length === 0 ? (
              <p className="text-sm text-slate-500">No leave data.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} label>
                    {pieData.map((_, i) => <Cell key={i} fill={pieColors[i % pieColors.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Department Headcount */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-slate-800">Department-wise Headcount</h2>
          <span className="text-xs text-slate-500">Active employees</span>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={deptBars}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Lists + Alerts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <InfoList title="New Joiners (Last 7 Days)" items={data?.lists?.newJoiners} type="joiners" />
        <div className="space-y-4">
          <AlertTable title="Late Check-ins (Today)" rows={data?.alerts?.lateAlerts} />
          <AlertTable title="Missing Check-out (Today)" rows={data?.alerts?.missingCheckout} />
          <AlertTable title="Overtime Spikes (Last 7 Days)" rows={data?.alerts?.overtimeSpikes} overtime />
        </div>
      </div>

      {loading && <div className="mt-6 text-sm text-slate-500">Loading dashboard…</div>}
    </HrLayout>
  );
}

/* ----- Small UI Components ----- */

function StatCard({ title, value, hint, accent }) {
  const accentCls = accent === "emerald"
    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
    : accent === "rose"
    ? "bg-rose-50 border-rose-200 text-rose-700"
    : accent === "amber"
    ? "bg-amber-50 border-amber-200 text-amber-700"
    : accent === "indigo"
    ? "bg-indigo-50 border-indigo-200 text-indigo-700"
    : "bg-white border-slate-200 text-slate-700";

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${accentCls}`}>
      <p className="text-[11px] font-bold uppercase tracking-wide opacity-80">{title}</p>
      <div className="mt-2 text-3xl font-extrabold">{value}</div>
      <p className="mt-1 text-xs opacity-80">{hint}</p>
    </div>
  );
}

function InfoList({ title, items, type }) {
  const safe = Array.isArray(items) ? items : [];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <h2 className="text-sm font-bold text-slate-800 mb-3">{title}</h2>
      {safe.length === 0 ? (
        <p className="text-sm text-slate-500">No data.</p>
      ) : (
        <div className="space-y-3">
          {safe.map((x) => {
            const name = `${x.first_name || ""} ${x.last_name || ""}`.trim() || "—";
            return (
              <div key={x.id} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{name}</div>
                  <div className="text-xs text-slate-500 truncate">{x.employee_code ? `Code: ${x.employee_code}` : ""}</div>
                </div>
                <div className="text-xs text-slate-600">
                  {type === "birthdays"
                    ? x.dob ? String(x.dob).slice(0, 10) : "—"
                    : x.join_date ? String(x.join_date).slice(0, 10) : "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AlertTable({ title, rows, overtime }) {
  const safe = Array.isArray(rows) ? rows : [];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        <span className="text-xs text-slate-500">{safe.length}</span>
      </div>
      {safe.length === 0 ? (
        <p className="text-sm text-slate-500">No alerts.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left py-2">Employee</th>
                <th className="text-left py-2">Info</th>
                {overtime && <th className="text-right py-2">Work (min)</th>}
              </tr>
            </thead>
            <tbody>
              {safe.map((r, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="py-2 text-slate-700 font-semibold">
                    {r.first_name} {r.last_name}
                    {r.employee_code && <span className="ml-2 text-[10px] text-slate-400">({r.employee_code})</span>}
                  </td>
                  <td className="py-2 text-slate-600">
                    {r.check_in ? `In: ${new Date(r.check_in).toLocaleTimeString()}` : ""}
                    {r.date ? ` • ${String(r.date).slice(0, 10)}` : ""}
                  </td>
                  {overtime && <td className="py-2 text-right font-bold text-slate-800">{r.work_hours ?? "--"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
